import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import React, { useEffect, useState } from "react";
import { Card, Col, Row, Button, ProgressBar } from "react-bootstrap";
import { useSelector } from "react-redux";
import axiosInstance from "../../axios/axiosConfig";
import {
    selectCriticalDataError,
    selectCriticalDataLoading,
} from "../../redux/slices/criticalDataSlice";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";

const CHANGE_RANGES = [
    { key: "24h", label: "24 Hours", days: 1 },
    { key: "7d", label: "7 Days", days: 7 },
    { key: "30d", label: "30 Days", days: 30 },
];

const getChangeDate = (log) => {
    const rawDate =
        log?.dateTime ||
        log?.DateTime ||
        log?.createdAt ||
        log?.CreatedAt ||
        log?.updatedAt ||
        log?.UpdatedAt;

    if (!rawDate) return null;

    const parsedDate = new Date(rawDate);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getTopEntries = (logs, keyCandidates) => {
    const counts = logs.reduce((acc, log) => {
        const matchKey = keyCandidates.find((key) => {
            const value = log?.[key];
            return typeof value === "string" && value.trim().length > 0;
        });

        const rawValue = matchKey ? log[matchKey] : "Unknown";
        const normalizedValue = String(rawValue || "Unknown").trim() || "Unknown";

        acc[normalizedValue] = (acc[normalizedValue] || 0) + 1;
        return acc;
    }, {});

    return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([label, count]) => ({ label, count }));
};


function AdminHome({ onNavigate, availableTabKeys = [] }) {
    const apiUrls = useSelector(selectApiUrls) || {};
    const criticalData = useSelector((state) => state.criticalData?.data) || {};
    const criticalDataLoading = useSelector(selectCriticalDataLoading);
    const criticalDataError = useSelector(selectCriticalDataError);

    const [selectedRange, setSelectedRange] = useState("7d");
    const [changeLogs, setChangeLogs] = useState([]);
    const [changeLogStatus, setChangeLogStatus] = useState("idle");
    const [changeLogError, setChangeLogError] = useState(null);
    useGuardedRealtimeList({ apiKey: 'changeLog', blocked: changeLogStatus === 'loading', apply: setChangeLogs });

    useEffect(() => {
        let isMounted = true;

        const loadChangeLogs = async () => {
            if (!apiUrls.changeLog) {
                if (isMounted) {
                    setChangeLogStatus("failed");
                    setChangeLogError("Change log API URL is not configured.");
                }
                return;
            }

            try {
                if (isMounted) {
                    setChangeLogStatus("loading");
                    setChangeLogError(null);
                }

                const response = await axiosInstance.get(apiUrls.changeLog);
                const logs = Array.isArray(response.data) ? response.data : [];

                if (isMounted) {
                    setChangeLogs(logs);
                    setChangeLogStatus("succeeded");
                }
            } catch (error) {
                if (isMounted) {
                    setChangeLogStatus("failed");
                    setChangeLogError(error?.message || "Failed to load change log.");
                }
            }
        };

        loadChangeLogs();

        return () => {
            isMounted = false;
        };
    }, [apiUrls.changeLog]);

    const now = new Date();
    const logsByRange = CHANGE_RANGES.reduce((acc, range) => {
        const cutoff = new Date(now);
        cutoff.setDate(cutoff.getDate() - range.days);

        acc[range.key] = changeLogs.filter((log) => {
            const date = getChangeDate(log);
            return date && date >= cutoff;
        });

        return acc;
    }, {});

    const selectedLogs = logsByRange[selectedRange] || [];
    const topUsers = getTopEntries(selectedLogs, ["currentUser", "CurrentUser", "user", "User", "changedBy", "ChangedBy"]);
    const topSections = getTopEntries(selectedLogs, ["section", "Section"]);
    const maxUserCount = Math.max(...topUsers.map((item) => item.count), 1);
    const maxSectionCount = Math.max(...topSections.map((item) => item.count), 1);

    const apiCount = Object.keys(apiUrls).length;
    const userPreferencesCount = Array.isArray(criticalData.userPreferences)
        ? criticalData.userPreferences.length
        : 0;
    const dropdownValueCount = Array.isArray(criticalData.dropdownValues)
        ? criticalData.dropdownValues.length
        : 0;
    const dropdownTypeCount = Array.isArray(criticalData.dropdownValues)
        ? new Set(
            criticalData.dropdownValues
                .map((item) => (item?.DropdownType || item?.dropdownType || "").trim())
                .filter((value) => value.length > 0)
        ).size
        : 0;

    const dashboardMetrics = [
        {
            label: "APIs Configured",
            value: apiCount,
            note: "Count from apiUrlSlice",
        },
        {
            label: "User Preferences",
            value: userPreferencesCount,
            note: criticalDataLoading
                ? "Loading from critical data"
                : criticalDataError
                    ? "Unable to load critical data"
                    : "Records from critical data",
        },
        {
            label: "Dropdown Values",
            value: `${dropdownTypeCount} dropdowns / ${dropdownValueCount} values`,
            note: criticalDataLoading
                ? "Loading from critical data"
                : criticalDataError
                    ? "Unable to load critical data"
                    : "Unique dropdown types and total value rows",
        },
    ];

    const quickActions = [
        { label: "Form Manager", eventKey: "formManager" },
        { label: "Users", eventKey: "users" },
        { label: "Component Policies", eventKey: "componentPolicies" },
        { label: "Manage Jobs", eventKey: "jobs" },
        { label: "Allocated vs Actual Time", eventKey: "timeComparison" },
        { label: "Field Search", eventKey: "fieldSearch" },
        { label: "API Test", eventKey: "apiTest" },
        { label: "SignalR Test", eventKey: "signalrTest" },
    ];

    const visibleQuickActions = availableTabKeys.length > 0
        ? quickActions.filter((action) => availableTabKeys.includes(action.eventKey))
        : quickActions;

    return (
        <div>
            <h2>Admin Home</h2>
            <p className="text-muted mb-4">
                Snapshot of key admin data and quick links to common tools.
            </p>

            <Row className="g-3 mb-4">
                {dashboardMetrics.map((metric) => (
                    <Col key={metric.label} xs={12} sm={6} lg={4}>
                        <Card className="h-100">
                            <Card.Body>
                                <Card.Subtitle className="mb-2 text-muted">{metric.label}</Card.Subtitle>
                                <Card.Title>{metric.value}</Card.Title>
                                <Card.Text className="mb-0 small">{metric.note}</Card.Text>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Card className="mb-4">
                <Card.Body>
                    <Card.Title className="mb-3">Change Log Analytics</Card.Title>

                    <div className="d-flex flex-wrap gap-2 mb-3">
                        {CHANGE_RANGES.map((range) => (
                            <Button
                                key={range.key}
                                variant={selectedRange === range.key ? "primary" : "outline-primary"}
                                size="sm"
                                onClick={() => setSelectedRange(range.key)}
                            >
                                {range.label} ({(logsByRange[range.key] || []).length})
                            </Button>
                        ))}
                    </div>

                    <Row className="g-3">
                        <Col xs={12} md={6}>
                            <Card className="h-100">
                                <Card.Body>
                                    <Card.Subtitle className="mb-2 text-muted">Top 5 Users ({selectedRange})</Card.Subtitle>
                                    {changeLogStatus === "loading" && <Card.Text className="mb-0 small">Loading change logs...</Card.Text>}
                                    {changeLogStatus === "failed" && <Card.Text className="mb-0 small">{changeLogError || "Unable to load change logs."}</Card.Text>}
                                    {changeLogStatus === "succeeded" && topUsers.length === 0 && (
                                        <Card.Text className="mb-0 small">No user activity found in this range.</Card.Text>
                                    )}
                                    {changeLogStatus === "succeeded" && topUsers.map((item) => (
                                        <div key={`user-${item.label}`} className="mb-2">
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span className="text-truncate me-2" title={item.label}>{item.label}</span>
                                                <span>{item.count}</span>
                                            </div>
                                            <ProgressBar now={(item.count / maxUserCount) * 100} />
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} md={6}>
                            <Card className="h-100">
                                <Card.Body>
                                    <Card.Subtitle className="mb-2 text-muted">Top 5 Sections ({selectedRange})</Card.Subtitle>
                                    {changeLogStatus === "loading" && <Card.Text className="mb-0 small">Loading change logs...</Card.Text>}
                                    {changeLogStatus === "failed" && <Card.Text className="mb-0 small">{changeLogError || "Unable to load change logs."}</Card.Text>}
                                    {changeLogStatus === "succeeded" && topSections.length === 0 && (
                                        <Card.Text className="mb-0 small">No section activity found in this range.</Card.Text>
                                    )}
                                    {changeLogStatus === "succeeded" && topSections.map((item) => (
                                        <div key={`section-${item.label}`} className="mb-2">
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span className="text-truncate me-2" title={item.label}>{item.label}</span>
                                                <span>{item.count}</span>
                                            </div>
                                            <ProgressBar now={(item.count / maxSectionCount) * 100} />
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <Card.Title className="mb-3">Quick Access</Card.Title>
                    <div className="d-flex flex-wrap gap-2">
                        {visibleQuickActions.map((action) => (
                            <Button
                                key={action.eventKey}
                                variant="outline-primary"
                                size="sm"
                                onClick={() => onNavigate && onNavigate(action.eventKey)}
                            >
                                {action.label}
                            </Button>
                        ))}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
}

export default AdminHome;
